// src/views/AttendanceMaster/AttendanceStudentListPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  Table,
  Spinner,
  Alert,
  Button,
} from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { apiService } from '../../constants/ApiService';
import { toast } from 'react-toastify';

import useVendorBranch from '../../utils/useVendorBranch';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import VendorBranchSelector from '../../components/VendorBranchSelector';

const AttendanceStudentListPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.authState);

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);


  const todayStr = new Date().toISOString().substring(0, 10);

  const [navState, setNavState] = useState(location.state ?? {});
  const [navBatch, setNavBatch] = useState((location.state ?? {})?.batch ?? {});

  const [classId, setClassId] = useState('');
  const [batchId, setBatchId] = useState('');
  const [date, setDate] = useState(todayStr);

  //  Attendance date (string)
  const [admDate, setAdmDate] = useState(todayStr);

  useEffect(() => {
    const st = location.state ?? {};
    const b = st?.batch ?? {};

    const clsId = b?.class_id ?? st?.classId ?? st?.class_id ?? '';
    const bId = b?.id ?? st?.batchId ?? st?.batch_id ?? '';
    const dt = st?.date ?? todayStr;

    setNavState(st);
    setNavBatch(b);

    setClassId(clsId);
    setBatchId(bId);
    setDate(dt);
    setAdmDate(dt);
   
  }, [location.state]);

  const [errorMessage, setErrorMessage] = useState({});

  
  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  const [search, setSearch] = useState('');

  const [students, setStudents] = useState([]);
  const [presentMap, setPresentMap] = useState({});
  const [reasonMap, setReasonMap] = useState({});
  const [reasonList, setReasonList] = useState([]);

  const [loadingStudents, setLoadingStudents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const getEnrollmentId = (s) =>
    s?.enrollmentId ?? s?.enrollment_id ?? s?.EnrollmentId ?? null;

  // ✅ Multi-purpose master (AttendanceReason filter)
  const Get_All_Multi_Purpose_Type_Master = async () => {
    try {
      let config = {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
      };

      const response = await apiService.get(
        `api/Multi_Purpose_Type_Master/Get_All_Multi_Purpose_Type_Master`,
        config
      );

      if (response.response && response.response.data.errors) {
        toast.error(response.response?.data?.errors);
      }

      if (response.status === true) {
        const all = response.data || [];

        //  AttendanceReason filter
        const reasons = all.filter(
          (item) =>
            String(item.multi_purpose_flag || '').toLowerCase() ===
            'attendancereason'
        );

        setReasonList(reasons);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.log(error);
      toast.error('SomeThing went wrong!!');
    }
  };

  useEffect(() => {
    Get_All_Multi_Purpose_Type_Master();
  }, []);

  // Students API (simple response style)
  const GetStudentsList = async (searchText = '', dateParam = null) => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';
      const usedDate = dateParam ?? admDate;

      setLoadingStudents(true);
      setError('');
      setSuccessMsg('');
      setAttendanceSummary(null);

      const response = await apiService.get(
        `api/AttendanceMaster/GetStudentsForAttendance?classId=${classId}&date=${usedDate}&batchId=${batchId}&vendorId=${vendorId}&branchId=${branchId}&search=${searchText}`
      );

      if (response.status === true) {
        const list = response.data || [];
        setStudents(list);

        //  present map
        const pMap = {};
        (list || []).forEach((s) => {
          const eid = getEnrollmentId(s);
          const present = s.isPresent ?? s.IsPresent ?? true;
          if (eid != null) pMap[eid] = !!present;
        });
        setPresentMap(pMap);
      } else {
        alert(response.message);
      }
    } catch (error) {
      console.log(error);
      alert('Something went wrong!!');
    } finally {
      setLoadingStudents(false);
    }
  };

  // ✅ auto load 
  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad && classId) {
      GetStudentsList(search, admDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectionData.vendor_id,
    selectionData.branch_id,
    classId,
    batchId,
    admDate,
  ]);

  const onHandleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    GetStudentsList(val, admDate);
  };

  const onHandleDate = (e) => {
    const val = e.target.value;
    setAdmDate(val);
    GetStudentsList(search, val); // ✅ use new date immediately
  };

  const setPresentStatus = (enrollmentId, isPresent) => {
    setPresentMap((prev) => ({ ...prev, [enrollmentId]: isPresent }));

    // ✅ present => reason clear
    if (isPresent === true) {
      setReasonMap((prev) => {
        const copy = { ...prev };
        delete copy[enrollmentId];
        return copy;
      });
    }
  };

  const setStudentReason = (enrollmentId, reasonText) => {
    setReasonMap((prev) => ({ ...prev, [enrollmentId]: reasonText }));
  };

  // ✅ Save API (simple response style)
  const handleSaveAttendance = async () => {
    setError('');
    setSuccessMsg('');
    setAttendanceSummary(null);

    if (!students.length) {
      setError('No students to save.');
      return;
    }

    const absentIds = students
      .map((s) => getEnrollmentId(s))
      .filter((eid) => eid != null && presentMap[eid] === false);

    const reasonsPayload = {};
    absentIds.forEach((id) => {
      const r = reasonMap[id];
      if (r != null && r !== '') reasonsPayload[id] = r;
    });

    const payload = {
      classId: Number(classId),
      batchId: batchId ? Number(batchId) : null,
      date: admDate, // ✅ use selected date
      absentStudentIds: absentIds,
      reasons: reasonsPayload,
    };

    try {
      setSaving(true);

      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      const config = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      const response = await apiService.post(
        `api/AttendanceMaster/save?vendorId=${vendorId}&branchId=${branchId}`,
        JSON.stringify(payload),
        config
      );

      if (response.status === true) {
        setSuccessMsg(response.message || 'Attendance saved');
        await fetchAttendanceSummary();
      } else {
        toast(response.message);
      }
    } catch (error) {
      console.log(error);
      toast('Something went wrong!!');
    } finally {
      setSaving(false);
    }
  };

  // ✅ View API 
  const fetchAttendanceSummary = async () => {
    try {
      setViewLoading(true);

      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      const response = await apiService.get(
        `api/AttendanceMaster/view?classId=${classId}&date=${admDate}&batchId=${batchId}&vendorId=${vendorId}&branchId=${branchId}`
      );

      if (response.status === true) {
        setAttendanceSummary(response.data || null);
      } else {
        alert(response.message);
      }
    } catch (error) {
      console.log(error);
      alert('Something went wrong!!');
    } finally {
      setViewLoading(false);
    }
  };

  const absenteesList =
    attendanceSummary?.absentees || attendanceSummary?.Absentees || [];

  const totalPresentCount = students.filter((s) => {
    const eid = getEnrollmentId(s);
    return eid != null && presentMap[eid] !== false;
  }).length;

  const totalAbsentCount = students.filter((s) => {
    const eid = getEnrollmentId(s);
    return eid != null && presentMap[eid] === false;
  }).length;

  return (
    <div className='container-fluid mt-3'>
      <Row className='mb-2'>
        <Col md='8'>
          <h5 className='mb-1'>
            Attendance List – Class: {classId} &nbsp; Date: {admDate}
          </h5>

          <input
            type='date'
            className='form-control'
            value={admDate}
            onChange={onHandleDate}
          />
        </Col>
        <Col md='4' className='text-right'>
          <Button color='secondary' size='sm' onClick={() => navigate(-1)}>
            « Back
          </Button>
        </Col>
      </Row>

      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      <Card className='card-box height-100-p pd-20 mb-20'>
        <CardBody>
          <div className='col-12 d-flex justify-content-center mb-2'>
            <Input
              placeholder='Search...'
              value={search}
              onChange={onHandleSearch}
              className='mx-auto'
            />
          </div>

          <div className='d-flex justify-content-between align-items-center mb-2'>
            <div>
              <strong>Total:</strong> {students.length} &nbsp; | &nbsp;
              <strong>Present:</strong> {totalPresentCount} &nbsp; | &nbsp;
              <strong>Absent:</strong> {totalAbsentCount}
            </div>

            <Button
              color='success'
              onClick={handleSaveAttendance}
              disabled={saving || loadingStudents}
            >
              {saving ? (
                <>
                  <Spinner size='sm' className='mr-2' /> Saving...
                </>
              ) : (
                'Save Attendance'
              )}
            </Button>
          </div>

          {error && <Alert color='danger'>{error}</Alert>}
          {successMsg && <Alert color='success'>{successMsg}</Alert>}

          {loadingStudents ? (
            <div className='text-center'>
              <Spinner /> Loading students...
            </div>
          ) : (
            <div className='table-responsive'>
              <Table bordered hover size='sm'>
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>#</th>
                    <th>Enroll No</th>
                    <th>Student Name</th>
                    <th style={{ width: '220px' }}>Attendance</th>
                    <th style={{ width: '220px' }}>Reason (if absent)</th>
                  </tr>
                </thead>
                <tbody>
                  {students.length > 0 ? (
                    students.map((stu, idx) => {
                      const eid =
                        getEnrollmentId(stu) ??
                        `${idx}_${stu?.studentId ?? ''}`;

                      const isAbsent = presentMap[eid] === false;
                      const reason = reasonMap[eid] ?? '';

                      return (
                        <tr key={eid}>
                          <td>{idx + 1}</td>
                          <td>
                            {stu.enrollmentNumber ??
                              stu.enrollment_number ??
                              stu.EnrollmentNumber ??
                              '-'}
                          </td>
                          <td>{stu.studentName ?? stu.student_name ?? '-'}</td>

                          <td>
                            <div className='d-flex align-items-center gap-3'>
                              <div className='form-check form-check-inline'>
                                <Input
                                  type='radio'
                                  name={`att_${eid}`}
                                  checked={isAbsent}
                                  onChange={() => setPresentStatus(eid, false)}
                                />
                                <Label className='ml-1 mb-0 text-danger'>
                                  Absent
                                </Label>
                              </div>

                              <div className='form-check form-check-inline'>
                                <Input
                                  type='radio'
                                  name={`att_${eid}`}
                                  checked={!isAbsent}
                                  onChange={() => setPresentStatus(eid, true)}
                                />
                                <Label className='ml-1 mb-0 text-primary'>
                                  Present
                                </Label>
                              </div>
                            </div>
                          </td>

                          <td>
                            <Input
                              type='select'
                              value={reason}
                              disabled={!isAbsent}
                              onChange={(e) =>
                                setStudentReason(eid, e.target.value)
                              }
                            >
                              <option value=''>-- Select Reason --</option>
                              {(reasonList || []).map((opt, i) => {
                                const text =
                                  opt.multi_purpose_type ||
                                  opt.multi_purpose_type_name ||
                                  opt.name ||
                                  '';
                                return (
                                  <option key={opt.id ?? i} value={text}>
                                    {text}
                                  </option>
                                );
                              })}
                            </Input>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan='5' className='text-center'>
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}

          <div className='d-flex justify-content-between align-items-center mt-3'>
            <h6 className='mb-0'>Attendance Summary</h6>
            <Button
              color='secondary'
              size='sm'
              onClick={fetchAttendanceSummary}
              disabled={viewLoading}
            >
              {viewLoading ? (
                <>
                  <Spinner size='sm' className='mr-2' /> Loading...
                </>
              ) : (
                'View'
              )}
            </Button>
          </div>

          {attendanceSummary ? (
            <div className='mt-2'>
              <p className='mb-1'>
                <strong>Total Students:</strong>{' '}
                {attendanceSummary.totalStudents ??
                  attendanceSummary.TotalStudents ??
                  0}
                &nbsp; | &nbsp;
                <strong>Present:</strong>{' '}
                {attendanceSummary.totalPresent ??
                  attendanceSummary.TotalPresent ??
                  0}
                &nbsp; | &nbsp;
                <strong>Absent:</strong>{' '}
                {attendanceSummary.totalAbsent ??
                  attendanceSummary.TotalAbsent ??
                  0}
              </p>

              {absenteesList.length > 0 && (
                <>
                  <h6 className='mt-3'>Absentees</h6>
                  <Table bordered hover size='sm'>
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>#</th>
                        <th>Enroll No</th>
                        <th>Student</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {absenteesList.map((a, idx) => (
                        <tr key={a.enrollmentId ?? a.EnrollmentId ?? idx}>
                          <td>{idx + 1}</td>
                          <td>
                            {a.enrollmentNumber ?? a.EnrollmentNumber ?? '-'}
                          </td>
                          <td>{a.studentName ?? a.StudentName ?? '-'}</td>
                          <td>{a.reason ?? a.Reason ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </div>
          ) : (
            <p className='text-muted mt-2'>
              No attendance summary found (or not yet saved).
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default AttendanceStudentListPage;
